import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  TablePagination,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import BarChartIcon from '@mui/icons-material/BarChart';
import axios from '../../services/axiosConfig';
import { environment } from '../../config/environment';
import { useAuth } from '../../context/AuthContext';

interface IEchoPayment {
  couponId: string;
  reference: string;
  amount: number;
  dateCreated: string;
  totalCount: number;
  sentCount: number;
  receivedCount: number;
  viewedCount: number;
  isDeleted: boolean;
}

const EchoPaymentsList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [payments, setPayments] = useState<IEchoPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { hasRole } = useAuth();

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        notification_type: 'echo_pay'
      };
      if (reference) params.reference = reference;

      const response = await axios.get(`${environment.apiUrl}coupon`, { params });
      console.log('Echo Payments response:', response.data);

      const paymentsData = Array.isArray(response.data) ? response.data : [];
      setPayments(paymentsData);
    } catch (error) {
      console.error('Error loading payments:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [reference]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const handleSearch = () => {
    setPage(0);
    loadPayments();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedPayments = payments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleHidePayment = async (payment: IEchoPayment) => {
    if (window.confirm('Are you sure you want to make this INACTIVE?')) {
      try {
        await axios.delete(`${environment.apiUrl}coupon/${payment.couponId}`);
        loadPayments();
      } catch (error) {
        console.error('Error hiding payment:', error);
        alert('Network error. Try again later');
      }
    }
  };

  const handleUnhidePayment = async (payment: IEchoPayment) => {
    if (window.confirm('Are you sure you want to make this ACTIVE again?')) {
      try {
        await axios.put(`${environment.apiUrl}coupon/${payment.couponId}`);
        loadPayments();
      } catch (error) {
        console.error('Error unhiding payment:', error);
        alert('Network error. Try again later');
      }
    }
  };

  const handleDeletePayment = async (payment: IEchoPayment) => {
    if (window.confirm('Are you sure you want to delete it permanently?')) {
      try {
        await axios.delete(`${environment.apiUrl}coupon/${payment.couponId}?force=true`);
        loadPayments();
      } catch (error) {
        console.error('Error deleting payment:', error);
        alert('Network error. Try again later');
      }
    }
  };

  const showDeleteButton = (payment: IEchoPayment) => {
    return payment.isDeleted && hasRole(['notification:admin', 'notification:tester']);
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.secondary' }} gutterBottom>
        Echo Payment Requests
      </Typography>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
          <TextField
            label="Reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            size="small"
            placeholder="Search by reference"
            sx={{ flex: '1 1 200px', minWidth: '150px' }}
          />
          <Button 
            variant="contained" 
            onClick={handleSearch}
            sx={{ minWidth: '120px', height: '40px' }}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {paginatedPayments.map((payment) => (
              <Card 
                key={payment.couponId} 
                sx={{ 
                  mb: 2, 
                  border: payment.isDeleted ? '1px solid' : 'none',
                  borderColor: payment.isDeleted ? 'error.main' : 'transparent',
                  backgroundColor: payment.isDeleted ? 'rgba(244, 67, 54, 0.08)' : 'background.paper'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: payment.isDeleted ? 'error.main' : 'inherit' }}>
                      {payment.reference}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton size="small" color="info">
                        <BarChartIcon fontSize="small" />
                      </IconButton>
                      {!payment.isDeleted ? (
                        <IconButton size="small" color="warning" onClick={() => handleHidePayment(payment)}>
                          <VisibilityOffIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton size="small" color="success" onClick={() => handleUnhidePayment(payment)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                      {showDeleteButton(payment) ? (
                        <IconButton size="small" color="error" onClick={() => handleDeletePayment(payment)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Amount:</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatAmount(payment.amount)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Date Created:</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {new Date(payment.dateCreated).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Total:</Typography>
                      <Typography variant="body2" fontWeight={600}>{payment.totalCount}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Sent:</Typography>
                      <Typography variant="body2" fontWeight={500}>{payment.sentCount}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Received:</Typography>
                      <Typography variant="body2" fontWeight={500}>{payment.receivedCount}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Viewed:</Typography>
                      <Typography variant="body2" fontWeight={500}>{payment.viewedCount}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={payments.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>

          {/* Desktop Table View */}
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', display: { xs: 'none', md: 'block' } }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650, tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Date Created</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 70 }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 70 }}>Sent</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Received</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 70 }}>Viewed</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {paginatedPayments.map((payment) => (
                  <TableRow
                    key={payment.couponId}
                    sx={{ 
                      color: payment.isDeleted ? 'error.main' : 'inherit',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <TableCell sx={{ color: payment.isDeleted ? 'error.main' : 'inherit', wordBreak: 'break-word' }}>
                      {payment.reference}
                    </TableCell>
                    <TableCell sx={{ color: payment.isDeleted ? 'error.main' : 'inherit' }}>
                      {formatAmount(payment.amount)}
                    </TableCell>
                    <TableCell sx={{ color: payment.isDeleted ? 'error.main' : 'inherit' }}>
                      {new Date(payment.dateCreated).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ color: payment.isDeleted ? 'error.main' : 'inherit' }}>
                      {payment.totalCount}
                    </TableCell>
                    <TableCell sx={{ color: payment.isDeleted ? 'error.main' : 'inherit' }}>
                      {payment.sentCount}
                    </TableCell>
                    <TableCell sx={{ color: payment.isDeleted ? 'error.main' : 'inherit' }}>
                      {payment.receivedCount}
                    </TableCell>
                    <TableCell sx={{ color: payment.isDeleted ? 'error.main' : 'inherit' }}>
                      {payment.viewedCount}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Statistics">
                          <IconButton size="small" color="info">
                            <BarChartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {!payment.isDeleted ? (
                          <Tooltip title="Make Inactive">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleHidePayment(payment)}
                            >
                              <VisibilityOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Make Active">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleUnhidePayment(payment)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {showDeleteButton(payment) ? (
                          <Tooltip title="Delete Permanently">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeletePayment(payment)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={payments.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            />
          </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default EchoPaymentsList;
